/**
 * bookingListModel — pure display + formatting helpers for the bookings LIST
 * view (BON-130). Framework-free so the derivations (status pill mapping,
 * source/total display, money + delta formatting, card/table labels) are
 * unit-testable in isolation and shared by the card, table, and KPI strip.
 */
import type { BookingListItem } from '../../api/bookingsList';

/** Booking status-pill palette tokens (all tailwind.config.js scales). */
export type PillTone = 'moss' | 'water' | 'clay' | 'stone' | 'pending';

export interface DisplayStatus {
  label: string;
  tone: PillTone;
}

/**
 * Status pill mapping (contract "displayStatus + tone"). A comp always reads
 * "Comp" (blue) regardless of its underlying lifecycle status; otherwise the
 * raw status drives the label + tone. Pending is an ADDED treatment (the
 * `pending` amber scale) — required by the AC even though it is absent from the
 * mock.
 */
export function displayStatus(item: Pick<BookingListItem, 'status' | 'isComp'>): DisplayStatus {
  if (item.isComp) return { label: 'Comp', tone: 'water' };
  switch (item.status) {
    case 'CONFIRMED':
      return { label: 'Confirmed', tone: 'moss' };
    // SUPERSEDED is the deploy-window alias — see the BookingStatus union.
    case 'SUPERSEDED':
    case 'REBOOKED':
      return { label: 'Rebooked', tone: 'water' };
    case 'REFUNDED':
      return { label: 'Refunded', tone: 'clay' };
    case 'CANCELLED':
      return { label: 'Canceled', tone: 'stone' };
    default:
      return { label: 'Unknown', tone: 'stone' };
  }
}

/** Coarse source label (contract "displaySource"). */
export function displaySource(item: Pick<BookingListItem, 'isComp' | 'source'>): string {
  if (item.isComp) return 'Comp';
  return item.source === 'fareharbor' ? 'FareHarbor' : 'Online';
}

/** Integer cents → localized currency string, e.g. `$145.80`. */
export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(cents / 100);
}

/**
 * Total column: comps show "Comp"; a genuine $0 total shows an em dash ("—");
 * everything else shows formatted money. Deliberately NOT special-cased on
 * SUPERSEDED — a rebooked original with a non-zero total shows that honest
 * total for now (the "should be $0" fix is data-side, tracked in BON-329).
 */
export function displayTotal(item: Pick<BookingListItem, 'isComp' | 'money'>): string {
  if (item.isComp) return 'Comp';
  if (item.money.totalCents === 0) return '—';
  return formatMoney(item.money.totalCents, item.money.currency);
}

/** Compact currency for KPI tiles, e.g. `$26.8K`. */
export function formatCompactMoney(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(cents / 100);
}

/** Signed WoW delta for KPI tiles; `null` (no prior week) → em dash. Normalizes
 *  a rounded `-0` to `0` so a near-zero negative delta reads "+0%", not "-0%". */
export function formatDeltaPct(pct: number | null): string {
  if (pct === null || Number.isNaN(pct)) return '—';
  const rounded = Math.round(pct) || 0; // `|| 0` collapses -0 → 0
  return `${rounded >= 0 ? '+' : ''}${rounded}%`;
}

/** Whether a delta reads as positive (drives the green/red tone). A delta that
 *  rounds to zero is treated as positive so its tone matches the "+0%" label. */
export function isPositiveDelta(pct: number | null): boolean {
  return pct !== null && Math.round(pct) >= 0;
}

/** Full customer name, falling back to email, then a placeholder. */
export function customerName(c: BookingListItem['customer']): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return name || c.email || 'Unknown';
}

/** "1 ticket" / "N tickets". */
export function ticketsLabel(n: number): string {
  return `${n} ${n === 1 ? 'ticket' : 'tickets'}`;
}

/**
 * Preferred display instant: the naive wall-clock local start (parsed as local,
 * which is the venue-local time we want) falling back to the UTC instant.
 */
function whenIso(w: BookingListItem['workshop']): string {
  return w.localStartDatetime ?? w.startsAt;
}

/** e.g. "Tue May 19". */
export function workshopDateLabel(w: BookingListItem['workshop']): string {
  const d = new Date(whenIso(w));
  if (Number.isNaN(d.getTime())) return '';
  // "Tue, May 19" → "Tue May 19" (drop the weekday comma to match the mock).
  return d
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    .replace(',', '');
}

/** e.g. "6:30p" — single-letter lowercase meridiem, matching the mock. */
export function workshopTimeLabel(w: BookingListItem['workshop']): string {
  const d = new Date(whenIso(w));
  if (Number.isNaN(d.getTime())) return '';
  const t = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  // `\s` matches the ASCII space AND the ICU narrow no-break space (U+202F)
  // that newer Node/ICU emit between the time and meridiem.
  return t.replace(/\s*AM$/, 'a').replace(/\s*PM$/, 'p');
}
